import { Box, Button, Chip, CircularProgress, Drawer, IconButton, Menu, MenuItem, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Select } from "@mui/material";
import { OpenInNew, MoreVert, Edit, ReceiptLong, Close } from "@mui/icons-material";
import { useChatPdVContext } from "../../../../contexts/atendimento/ChatPdVContext";
import { IPedido } from "../../../../interface/IPedido";
import { StatusPedido, StatusPedidosCores, StatusPedidoDescricao } from "../../../../enum/StatusPedido";
import { TipoPedido } from "../../../../enum/TipoPedido";
import { TipoMarketplace } from "../../../../../marketplace/enum/TipoMarketplace";
import { useDataHora } from "../../../../../core/hooks/useDataHora";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { usePedidos } from "../../../../hooks/usePedidos";
import { useEntregadores } from "../../../../../entregadores/hooks/useEntregadores";
import { IEntregador } from "../../../../../entregadores/interfaces/IEntregador";
import { useState, useEffect } from "react";
import { ModalCancelarPedido } from "../../../../pages/ListagemPedidos/containers/FerramentasPedidos/components/ModalCancelarPedido";
import { alpha } from "@mui/material/styles";
import { PedidoDetalhado } from "../../../../containers/PedidoDetalhado";
import { useTamanhoTela } from "../../../../../core/hooks/useTamanhoTela";
import { acaoPrincipal, contorno, cores, raio, superficie, texto } from "../../identidade";

const getStatusText = (status: StatusPedido): string => {
    return StatusPedido[status] as keyof typeof StatusPedido;
};

const getStatusColor = (status: StatusPedido): string => {
    const statusText = getStatusText(status) as keyof typeof StatusPedidosCores;
    return StatusPedidosCores[statusText] || "#000";
};

export const ChatPdVPedidosEmAndamento = () => {
    const { 
        pedidosEmAndamento, 
        loadingPedidosEmAndamento, 
        setExibirNovoPedido,
        buscarPedidosEmAndamento,
        cliente
    } = useChatPdVContext();
    const { dataHoraBr } = useDataHora();
    const { real } = useMoeda();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const chatUuid = searchParams.get('chat');
    const { atualizarStatus, loading: loadingAtualizarStatus } = usePedidos();
    const { listar: listarEntregadores, loading: loadingEntregadores } = useEntregadores();
    
    const [menuAnchorEl, setMenuAnchorEl] = useState<{element: HTMLElement, pedido: IPedido} | null>(null);
    const [cancelarOpen, setCancelarOpen] = useState(false);
    const [pedidoParaCancelar, setPedidoParaCancelar] = useState<IPedido | undefined>(undefined);
    const [dialogEntregadorOpen, setDialogEntregadorOpen] = useState(false);
    const [pedidoParaDespachar, setPedidoParaDespachar] = useState<IPedido | undefined>(undefined);
    const [entregadores, setEntregadores] = useState<IEntregador[]>([]);
    const [entregadorSelecionado, setEntregadorSelecionado] = useState<string>("");
    const [uuidPedidoDetalhes, setUuidPedidoDetalhes] = useState<string | undefined>(undefined);
    const { modeloTela } = useTamanhoTela();

    const formatarValor = (valor: number) => {
        return real(valor);
    };

    const handleAbrirPedido = (pedidoUuid: string) => {
        const redirectUrl = chatUuid 
            ? `/pedidos/conversas?chat=${chatUuid}`
            : '/pedidos/conversas';
        navigate(`/pedidos/operacao/edicao/${pedidoUuid}?redirect=${encodeURIComponent(redirectUrl)}`);
    };

    const abrirDetalhes = (pedidoUuid: string) => setUuidPedidoDetalhes(pedidoUuid);
    const fecharDetalhes = () => setUuidPedidoDetalhes(undefined);

    /** Recarrega a lista quando o drawer altera algo no pedido. */
    const recarregarAposDetalhes = async () => {
        if (cliente?.uuid) {
            await buscarPedidosEmAndamento(cliente.uuid);
        }
    };

    const handleAbrirPedidoNovaAba = (pedidoUuid: string) => {
        window.open(`/pedidos/operacao/edicao/${pedidoUuid}`, '_blank');
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, pedido: IPedido) => {
        event.stopPropagation();
        setMenuAnchorEl({ element: event.currentTarget, pedido });
    };

    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
    };

    const handleAtualizarStatus = async (pedido: IPedido, status: StatusPedidoDescricao, entregador?: IEntregador) => {
        await atualizarStatus({
            pedidoUuid: pedido.uuid,
            entregador,
            status
        });
        
        if (cliente?.uuid) {
            await buscarPedidosEmAndamento(cliente.uuid);
        }
    };

    const handleCancelarPedido = (pedido: IPedido) => {
        setPedidoParaCancelar(pedido);
        setCancelarOpen(true);
    };

    const handleDespacharPedido = (pedido: IPedido) => {
        setPedidoParaDespachar(pedido);
        setDialogEntregadorOpen(true);
    };

    const handleConfirmarDespacho = async () => {
        if (!pedidoParaDespachar) return;
        
        const entregador = entregadores.find(e => e.uuid === entregadorSelecionado);
        await atualizarStatus({
            pedidoUuid: pedidoParaDespachar.uuid,
            status: StatusPedidoDescricao["Em transporte"],
            entregador
        });
        
        setDialogEntregadorOpen(false);
        setPedidoParaDespachar(undefined);
        setEntregadorSelecionado("");
        
        if (cliente?.uuid) {
            await buscarPedidosEmAndamento(cliente.uuid);
        }
    };

    const handleBuscarEntregadores = async () => {
        const entregadoresList = await listarEntregadores();
        setEntregadores(entregadoresList || []);
    };

    useEffect(() => {
        if (dialogEntregadorOpen) {
            handleBuscarEntregadores();
        }
    }, [dialogEntregadorOpen]);

    const getStatusOptions = (pedido: IPedido) => {
        const status = pedido.status as StatusPedido;
        const tipo = pedido.tipo as TipoPedido;
        const marketplace = pedido.marketplace;

        if (status === StatusPedido["Em andamento"]) {
            return [
                { label: "Concluir pedido", action: () => { handleCloseMenu(); handleAtualizarStatus(pedido, StatusPedidoDescricao["Concluído"]); } },
                { label: "Cancelar pedido", action: () => { handleCloseMenu(); handleCancelarPedido(pedido); }, isCancel: true }
            ];
        }

        if (status === StatusPedido["Em preparo"] && tipo === TipoPedido.delivery) {
            return [
                { label: "Despachar pedido", action: () => { handleCloseMenu(); handleDespacharPedido(pedido); } },
                { label: "Cancelar pedido", action: () => { handleCloseMenu(); handleCancelarPedido(pedido); }, isCancel: true }
            ];
        }

        if (status === StatusPedido["Em preparo"] && tipo === TipoPedido.retirada) {
            return [
                { label: "Pronto para coleta", action: () => { handleCloseMenu(); handleAtualizarStatus(pedido, StatusPedidoDescricao["Pronto para retirada"]); } },
                { label: "Cancelar pedido", action: () => { handleCloseMenu(); handleCancelarPedido(pedido); }, isCancel: true }
            ];
        }

        if (status === StatusPedido["Em transporte"]) {
            const options: Array<{ label: string; action: () => void; isCancel: boolean }> = [
                { label: "Cancelar pedido", action: () => { handleCloseMenu(); handleCancelarPedido(pedido); }, isCancel: true }
            ];
            if (marketplace?.tipo !== TipoMarketplace.IFOOD) {
                options.unshift({ label: "Concluir pedido", action: () => { handleCloseMenu(); handleAtualizarStatus(pedido, StatusPedidoDescricao["Concluído"]); }, isCancel: false });
            }
            return options;
        }

        if (status === StatusPedido["Pronto para retirada"]) {
            const options: Array<{ label: string; action: () => void; isCancel: boolean }> = [
                { label: "Cancelar pedido", action: () => { handleCloseMenu(); handleCancelarPedido(pedido); }, isCancel: true }
            ];
            if (marketplace?.tipo !== TipoMarketplace.IFOOD) {
                options.unshift({ label: "Concluir pedido", action: () => { handleCloseMenu(); handleAtualizarStatus(pedido, StatusPedidoDescricao["Concluído"]); }, isCancel: false });
            }
            return options;
        }

        if (status === StatusPedido["Aguardando confirmação"] || status === StatusPedido["Aguardando pagamento"]) {
            return [
                { label: "Confirmar pedido", action: () => { handleCloseMenu(); handleAtualizarStatus(pedido, StatusPedidoDescricao["Em preparo"]); } },
                { label: "Cancelar pedido", action: () => { handleCloseMenu(); handleCancelarPedido(pedido); }, isCancel: true }
            ];
        }

        return [];
    };

    const handleNovoPedido = () => {
        setExibirNovoPedido(true);
    };

    if (loadingPedidosEmAndamento) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, height: '100%' }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (!pedidosEmAndamento || pedidosEmAndamento.length === 0) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 1.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <ReceiptLong sx={{ fontSize: 16, color: cores.marca[400] }} />
                <Typography sx={{ ...texto.secao, color: 'text.secondary' }}>
                    Pedidos em andamento · {pedidosEmAndamento.length}
                </Typography>
            </Box>

            <Box sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}>
                {pedidosEmAndamento.map((pedido: IPedido, index: number) => {
                    const statusOptions = getStatusOptions(pedido);
                    const menuOpen = menuAnchorEl?.pedido?.uuid === pedido.uuid;
                    
                    return (
                        <Box
                            key={pedido.uuid}
                            sx={{
                                px: 1.5,
                                py: 1.5,
                                borderRadius: raio.md,
                                border: theme => contorno(theme, 0.08),
                                backgroundColor: theme => superficie(theme, 0.03),
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                {/* Identificação e valor à esquerda, estado e ações à direita */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ ...texto.titulo }}>
                                            Pedido #{pedido.numeroPedido || pedido.numeroPedidoDiario || '-'}
                                        </Typography>
                                        <Typography sx={{ ...texto.apoio, color: 'text.secondary', mt: 0.25 }}>
                                            {dataHoraBr(pedido.dataHora)}
                                        </Typography>
                                        <Typography sx={{ fontSize: '15px', fontWeight: 800, letterSpacing: '-0.01em', mt: 0.5 }}>
                                            {formatarValor(pedido.valorTotal)}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={getStatusText(pedido.status as StatusPedido)}
                                        size="small"
                                        sx={{
                                            flexShrink: 0,
                                            height: 22,
                                            borderRadius: raio.pill,
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            letterSpacing: '0.03em',
                                            color: getStatusColor(pedido.status as StatusPedido),
                                            backgroundColor: alpha(getStatusColor(pedido.status as StatusPedido), 0.14),
                                            border: `1px solid ${alpha(getStatusColor(pedido.status as StatusPedido), 0.35)}`,
                                            '& .MuiChip-label': { px: 0.9 },
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => abrirDetalhes(pedido.uuid)}
                                            title="Ver pedido detalhado"
                                            sx={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: raio.sm,
                                                color: cores.marca[500],
                                                backgroundColor: alpha(cores.marca[500], 0.1),
                                                '&:hover': { backgroundColor: alpha(cores.marca[500], 0.18) },
                                            }}
                                        >
                                            <ReceiptLong sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleAbrirPedido(pedido.uuid)}
                                            title="Editar pedido"
                                            sx={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: raio.sm,
                                                color: 'text.secondary',
                                                backgroundColor: theme => superficie(theme, 0.06),
                                                '&:hover': { backgroundColor: theme => superficie(theme, 0.12) },
                                            }}
                                        >
                                            <Edit sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleAbrirPedidoNovaAba(pedido.uuid)}
                                            title="Abrir em nova aba"
                                            sx={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: raio.sm,
                                                color: 'text.secondary',
                                                backgroundColor: theme => superficie(theme, 0.06),
                                                '&:hover': { backgroundColor: theme => superficie(theme, 0.12) },
                                            }}
                                        >
                                            <OpenInNew sx={{ fontSize: 15 }} />
                                        </IconButton>
                                        {statusOptions.length > 0 && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleOpenMenu(e, pedido)}
                                                    title="Mudar status"
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        ml: 'auto',
                                                        borderRadius: raio.sm,
                                                        color: 'text.secondary',
                                                        backgroundColor: theme => superficie(theme, 0.06),
                                                        '&:hover': { backgroundColor: theme => superficie(theme, 0.12) },
                                                    }}
                                                >
                                                    <MoreVert sx={{ fontSize: 16 }} />
                                                </IconButton>
                                                <Menu
                                                    anchorEl={menuAnchorEl?.pedido?.uuid === pedido.uuid ? menuAnchorEl.element : null}
                                                    open={menuOpen}
                                                    onClose={handleCloseMenu}
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal: 'right',
                                                    }}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'right',
                                                    }}
                                                >
                                                    {statusOptions.map((option, idx) => (
                                                        <MenuItem
                                                            key={idx}
                                                            onClick={option.action}
                                                            sx={option.isCancel ? { color: 'error.main' } : {}}
                                                        >
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </Menu>
                                            </>
                                        )}
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            <Button
                variant="contained"
                fullWidth
                onClick={handleNovoPedido}
                sx={theme => ({
                    ...acaoPrincipal(theme),
                    flexShrink: 0,
                    height: 44,
                    borderRadius: raio.sm,
                    fontSize: '12.5px',
                })}
            >
                Novo pedido
            </Button>

            {/* Pedido detalhado no mesmo drawer usado na tela de Operação.
                zIndex acima do drawer de checkout do chat (10000). */}
            {uuidPedidoDetalhes && (
                <Drawer
                    anchor="right"
                    open
                    onClose={fecharDetalhes}
                    sx={{ zIndex: 10001 }}
                >
                    <Box
                        sx={{
                            width: modeloTela === 'desktop' ? '60vw' : '100vw',
                            padding: '1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                        }}
                    >
                        <IconButton onClick={fecharDetalhes}>
                            <Close />
                        </IconButton>
                        <PedidoDetalhado
                            uuid={uuidPedidoDetalhes}
                            onStatusAtualizado={recarregarAposDetalhes}
                            dialogZIndex={10002}
                        />
                    </Box>
                </Drawer>
            )}

            <ModalCancelarPedido
                open={cancelarOpen}
                fechar={() => {
                    setCancelarOpen(false);
                    setPedidoParaCancelar(undefined);
                }}
                atualizarStatus={async (props: any) => {
                    await atualizarStatus(props);
                    if (cliente?.uuid) {
                        await buscarPedidosEmAndamento(cliente.uuid);
                    }
                }}
                loading={loadingAtualizarStatus}
                pedido={pedidoParaCancelar}
            />

            <Dialog open={dialogEntregadorOpen} onClose={() => {
                setDialogEntregadorOpen(false);
                setPedidoParaDespachar(undefined);
                setEntregadorSelecionado("");
            }}>
                <DialogTitle>Selecionar entregador</DialogTitle>
                <DialogContent sx={{ minWidth: '400px' }}>
                    {loadingEntregadores ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={30} />
                        </Box>
                    ) : entregadores.length ? (
                        <Select
                            fullWidth
                            value={entregadorSelecionado}
                            onChange={(event) => setEntregadorSelecionado(event.target.value)}
                            displayEmpty
                        >
                            <MenuItem value="" disabled>Selecione um entregador</MenuItem>
                            {entregadores.map(entregador => (
                                <MenuItem key={entregador.uuid} value={entregador.uuid}>
                                    {entregador.nome} - {entregador.telefone}
                                </MenuItem>
                            ))}
                        </Select>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                            <Typography>
                                Nenhum entregador cadastrado
                            </Typography>
                            <Button onClick={() => navigate('/entregadores')}>Cadastrar entregador</Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setDialogEntregadorOpen(false);
                        setPedidoParaDespachar(undefined);
                        setEntregadorSelecionado("");
                    }}>Cancelar</Button>
                    <Button onClick={handleConfirmarDespacho} variant="contained">
                        {!entregadorSelecionado ? "Continuar sem entregador" : "Confirmar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

